(function (global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.Lightbox = global.NewHomeSource.Lightbox || {};

  if (!global.NewHomeSource.Modules || !global.NewHomeSource.Modules.ImageZoom) {
    return;
  }

  const CANVAS_CLASS = 'nhs-n1-c-zoom__canvas';
  const SLIDE_MEDIA_CLASS = 'nhs-n1-c-gallery__column--floorplan-zoom';
  const ZOOM_CONTROLS_SELECTOR = '[data-component="zoom-controls"]';
  const ZOOM_IN_SELECTOR = '[data-component="zoom-in"]';
  const ZOOM_OUT_SELECTOR = '[data-component="zoom-out"]';
  const PICTURE_HEIGHT_OFFSET = 40;
  const LANDSCAPE_WIDTH_OFFSET = 200;

  class Zoom {
    /**
     * Initializes a new instance of the lightbox Zoom handler.
     */
    constructor() {
      this.state = null;
      this.imageZoom = null;
      this.canvasElement = null;
      this.activeSlide = null;
      this.activePicture = null;
      this.currentIndex = null;
      this.zoomControls = null;
      this.zoomInButton = null;
      this.zoomOutButton = null;
      this.resizeObserver = null;
      this.eventsToObserve = {
        itemsChange: this.itemsChange.bind(this),
      };
      this.onZoomIn = this.onZoomIn.bind(this);
      this.onZoomOut = this.onZoomOut.bind(this);
      this.onResize = this.onResize.bind(this);
    }

    /**
     * Initializes the handler with the lightbox state, locates the zoom control
     * buttons inside the lightbox root, and wires up DOM/event listeners.
     *
     * @param {Object} state - The lightbox state instance.
     * @returns {this} The current instance for chaining.
     */
    initialize(state) {
      this.state = state;
      const rootNode = this.state.get('rootNode');
      this.zoomControls = rootNode.querySelector(ZOOM_CONTROLS_SELECTOR);
      this.zoomInButton = rootNode.querySelector(ZOOM_IN_SELECTOR);
      this.zoomOutButton = rootNode.querySelector(ZOOM_OUT_SELECTOR);
      if (this.zoomInButton) {
        this.zoomInButton.addEventListener('click', this.onZoomIn);
      }
      if (this.zoomOutButton) {
        this.zoomOutButton.addEventListener('click', this.onZoomOut);
      }
      if (typeof global.ResizeObserver === 'function') {
        this.resizeObserver = new global.ResizeObserver(this.onResize);
      } else {
        global.addEventListener('resize', this.onResize);
      }
      this.addObservers();
      return this;
    }

    /**
     * Handles a slide change event. Enables the zoom canvas for slides marked
     * with `data-zoomable="true"` and disables it for any other slide.
     *
     * @param {Object} data - Event payload from the lightbox state.
     * @param {number} data.currentIndex - The index of the currently active slide.
     */
    itemsChange(data) {
      if (this.currentIndex === data.currentIndex) {
        return;
      }
      this.currentIndex = data.currentIndex;
      const rootNode = this.state.get('rootNode');
      const slide = rootNode.querySelector(`[data-column="${data.currentIndex}"]`);
      if (!slide) {
        this.disableZoom();
        return;
      }
      if (slide.dataset.zoomable !== 'true') {
        this.disableZoom();
        return;
      }
      const item = this.state.findItemById(slide.dataset.mediaId);
      if (!item || !item.Url) {
        this.disableZoom();
        return;
      }
      this.enableZoom(slide, item);
    }

    /**
     * Replaces the slide's `<picture>` with a zoom-enabled `<canvas>` and
     * mounts a new ImageZoom instance for the given slide.
     *
     * @param {HTMLElement} slide - The zoomable slide column element.
     * @param {Object} item - The lightbox data item describing the image.
     */
    enableZoom(slide, item) {
      this.disableZoom();
      this.activeSlide = slide;
      this.activePicture = slide.querySelector('picture');
      const canvas = global.document.createElement('canvas');
      canvas.classList.add(CANVAS_CLASS);
      canvas.style.display = 'none';
      Zoom.blockCarouselDrag(canvas);
      this.canvasElement = canvas;
      slide.classList.add(SLIDE_MEDIA_CLASS);
      slide.prepend(canvas);
      if (this.resizeObserver) {
        this.resizeObserver.observe(slide);
      }

      Zoom.getCanvasSize(slide).then((size) => {
        if (this.canvasElement !== canvas) {
          return;
        }
        canvas.setAttribute('width', size.width);
        canvas.setAttribute('height', size.height);

        this.imageZoom = new global.NewHomeSource.Modules.ImageZoom(canvas, item.Url);

        this.imageZoom.picture.addEventListener('load', () => {
          if (this.canvasElement !== canvas) {
            return;
          }
          if (this.activePicture) {
            this.activePicture.style.display = 'none';
          }
          canvas.style.display = 'block';
          this.showControls();
        });

        this.imageZoom.picture.addEventListener('error', () => {
          if (this.canvasElement !== canvas) {
            return;
          }
          this.fallbackToPicture();
        });
      });
    }

    /**
     * Tears down any active zoom canvas, restores the original `<picture>`,
     * and hides the zoom controls. Safe to call when no zoom is active.
     */
    disableZoom() {
      this.hideControls();
      if (this.resizeObserver && this.activeSlide) {
        this.resizeObserver.unobserve(this.activeSlide);
      }
      if (this.canvasElement && this.canvasElement.parentElement) {
        this.canvasElement.parentElement.removeChild(this.canvasElement);
      }
      if (this.activeSlide) {
        this.activeSlide.classList.remove(SLIDE_MEDIA_CLASS);
      }
      if (this.activePicture) {
        this.activePicture.style.display = '';
      }
      this.canvasElement = null;
      this.activeSlide = null;
      this.activePicture = null;
      this.imageZoom = null;
    }

    /**
     * Restores the original `<picture>` element when the floor plan image
     * fails to load through the canvas pipeline.
     */
    fallbackToPicture() {
      if (this.resizeObserver && this.activeSlide) {
        this.resizeObserver.unobserve(this.activeSlide);
      }
      if (this.canvasElement && this.canvasElement.parentElement) {
        this.canvasElement.parentElement.removeChild(this.canvasElement);
      }
      if (this.activePicture) {
        this.activePicture.style.display = '';
      }
      if (this.activeSlide) {
        this.activeSlide.classList.remove(SLIDE_MEDIA_CLASS);
      }
      this.hideControls();
      this.canvasElement = null;
      this.imageZoom = null;
    }

    /**
     * Click handler for the zoom-in button.
     */
    onZoomIn() {
      if (this.imageZoom) {
        this.imageZoom.zoomIn();
      }
    }

    /**
     * Click handler for the zoom-out button.
     */
    onZoomOut() {
      if (this.imageZoom) {
        this.imageZoom.zoomOut();
      }
    }

    /**
     * Recomputes the canvas size from the active slide and asks the ImageZoom
     * instance to redraw at the new dimensions.
     */
    onResize() {
      if (!this.imageZoom || !this.activeSlide || !this.canvasElement) {
        return;
      }
      const canvas = this.canvasElement;
      const slide = this.activeSlide;
      Zoom.getCanvasSize(slide).then((size) => {
        if (this.canvasElement !== canvas || !this.imageZoom) {
          return;
        }
        canvas.setAttribute('width', size.width);
        canvas.setAttribute('height', size.height);
        this.imageZoom.resize(size.width, size.height);
      });
    }

    /**
     * Reveals the zoom in/out buttons.
     */
    showControls() {
      if (this.zoomControls) {
        this.zoomControls.removeAttribute('hidden');
      }
    }

    /**
     * Hides the zoom in/out buttons.
     */
    hideControls() {
      if (this.zoomControls) {
        this.zoomControls.setAttribute('hidden', '');
      }
    }

    /**
     * Calculates the canvas size for a given slide, accounting for landscape
     * orientation on touch devices the same way the legacy zoom module did.
     * Reads layout inside `requestAnimationFrame` to avoid layout thrashing.
     *
     * @param {HTMLElement} slide - The slide column element.
     * @returns {Promise<{width: number, height: number}>} The canvas dimensions in px.
     */
    static getCanvasSize(slide) {
      return new Promise((resolve) => {
        global.requestAnimationFrame(() => {
          const rect = slide.getBoundingClientRect();
          const isLandscape = global.window.orientation === -90 || global.window.orientation === 90;
          const width = isLandscape ? Math.max(rect.width - LANDSCAPE_WIDTH_OFFSET, 0) : rect.width;
          const height = Math.max(rect.height - PICTURE_HEIGHT_OFFSET, 0);
          resolve({ width, height });
        });
      });
    }

    /**
     * Prevents the carousel's delegated drag listeners (bound to the carousel
     * root) from receiving pointer/touch events that originate on the zoom
     * canvas, so panning the image doesn't also swipe the carousel.
     *
     * @param {HTMLCanvasElement} canvas - The zoom canvas element.
     */
    static blockCarouselDrag(canvas) {
      const stop = (evt) => evt.stopPropagation();
      canvas.addEventListener('touchstart', stop, { passive: true });
      canvas.addEventListener('mousedown', stop);
      canvas.addEventListener('pointerdown', stop);
      canvas.addEventListener('dragstart', stop);
    }

    /**
     * Registers this instance as an observer for each event in `eventsToObserve`.
     */
    addObservers() {
      Object.keys(this.eventsToObserve).forEach((event) => this.state.addObserver(event, this));
    }

    /**
     * Removes this instance from all observed events.
     */
    removeObservers() {
      Object.keys(this.eventsToObserve).forEach((event) => this.state.removeObserver(event, this));
    }

    /**
     * Routes a state notification to the appropriate handler.
     *
     * @param {*} data - Event payload.
     * @param {string} topic - The event topic.
     */
    update(data, topic) {
      const callback = this.eventsToObserve[topic];
      if (callback) {
        callback(data);
      }
    }

    /**
     * Tears down all DOM listeners, observers and the active zoom instance.
     */
    destroy() {
      this.disableZoom();
      if (this.zoomInButton) {
        this.zoomInButton.removeEventListener('click', this.onZoomIn);
      }
      if (this.zoomOutButton) {
        this.zoomOutButton.removeEventListener('click', this.onZoomOut);
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      } else {
        global.removeEventListener('resize', this.onResize);
      }
      this.removeObservers();
      this.zoomControls = null;
      this.zoomInButton = null;
      this.zoomOutButton = null;
      this.currentIndex = null;
    }
  }

  global.NewHomeSource.Lightbox.Zoom = Zoom;
}(window));
